

// on join session
//   navigate to session
//     -+ initialize video session component
//     -* browser: display an error message (user will close tab manually?)
//     -* cordova: navigate back and display toast message that an error occurred

// on leave/end session
//   cordova: navigate to previous view
//   browser: close current tab


import {stateGo} from 'redux-ui-router';
import {AbstractEpics} from '../../core-utilities/redux/AbstractEpics';
import {epicErrorHandler} from '../../core-utilities/rx-utils';
import {isWebBuild} from '../../config/constants';
import {VideoSessionService} from '../services/VideoSessionService';
import {commentsStates} from '../router';


export class VideoSessionEpics extends AbstractEpics {
  /**
   * @param options
   * @param {VideoSessionActions} actions
   */
  constructor(options, actions) {
    super(options);

    /** @type {VideoSessionActions} */
    this.actions = actions;

    const dependencies = [
      VideoSessionService.NAME,
      '$state',
    ];

    this.onStartSession = this.onStartSession.bind(this);
    this.onNavToVideo = this.onNavToVideo.bind(this);
    this.onGetSessionToken = this.onGetSessionToken.bind(this);
    this.onJoinSession = this.onJoinSession.bind(this);
    this.onLeaveSession = this.onLeaveSession.bind(this);

    this.onStartSession.$inject = dependencies;
    this.onNavToVideo.$inject = dependencies;
    this.onGetSessionToken.$inject = dependencies;
    this.onJoinSession.$inject = dependencies;

    this.addEpic(this.onStartSession);
    this.addEpic(this.onNavToVideo);
    this.addEpic(this.onGetSessionToken);
    this.addEpic(this.onJoinSession);
    this.addEpic(this.onLeaveSession);
  }

  /**
   * // TODO: we should probably include a session token for the user the started the session in the first place so that they can skip the extra step of requesting one. If so, dispatch receivedSessionToken({sessionToken})
   * on start session
   *   send request
   *     -+ navigate to session
   *     -* display toast error message
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param dependencies
   */
  onStartSession(action$, store, dependencies) {
    // response will have new session ID
    return action$.ofType(
      this.actions.createSession.requestSuccess.TYPE,
      this.actions.startSession.requestSuccess.TYPE,
    )
      // nav user to session
      .map(action => {
        const {
          channelId,
          sessionId,
          sessionToken,
        } = action.payload.ajaxResponse.response;
        const params = {channelId, sessionId};

        if (sessionToken)
          dependencies.videoSessionService.setToken(sessionId, sessionToken);

        return this.actions.navToVideo(params);
      })
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onSendRequest'));
  }

  onNavToVideo(action$, store, dependencies) {
    return action$.ofType(this.actions.navToVideo.TYPE)
      .map(action => {
        if (isWebBuild()) {
          const url = dependencies.$state.href(commentsStates.videoChat, action.payload);
          window.open(url, '_blank');
          return null;
        } else {
          return stateGo(commentsStates.videoChat, action.payload);
        }
      })
      .filter(Boolean)
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onNavToVideo'));
  }

  // TODO: display error message somewhere. maybe a toast?
  onStartSessionError(action$, store, dependencies) {
    return action$.ofType(this.actions.createSession.requestError.TYPE)
      .filter(() => false)
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onStartSessionError'));
  }

  /**
   * Retrieve the sessionToken from local cache if available, otherwise request a new one.
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param dependencies
   */
  onGetSessionToken(action$, store, dependencies) {
    return action$.ofType(this.actions.getSessionToken.TYPE)
      .map(action => {
        const channelId = action.payload.channelId;
        const sessionId = action.payload.sessionId;
        const sessionToken = dependencies.videoSessionService.getToken(sessionId);
        if (sessionToken) {
          return this.actions.receivedSessionToken({sessionToken});
        } else {
          return this.actions.joinSession.sendRequest({channelId});
        }
      })
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onJoinSession'));
  }

  /**
   * after navigating to a session
   * automatically attempt to join it
   *   -+ display video session componemt
   *   -* display error
   *
   * // previous idea, mostly scrapped
   * on join session
   *   navigate to session
   *     -+ initialize video session component
   *     -* browser: display an error message (user will close tab manually?)
   *     -* cordova: navigate back and display toast message that an error occurred
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param dependencies
   */
  onJoinSession(action$, store, dependencies) {
    return action$.ofType(this.actions.joinSession.requestSuccess.TYPE)
      .map(action => {
        const {
          sessionId,
          sessionToken,
        } = action.payload.ajaxResponse.response;
        dependencies.videoSessionService.setToken(sessionId, sessionToken);

        return this.actions.receivedSessionToken({sessionToken});
      })
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onJoinSession'));
  }

  /**
   * TODO: handle leaving the session properly. Bear in mind that most of the logic is handled with opentok SDK so we only need to nav and stuff.
   * on leave/end session
   *   cordova: navigate to previous view
   *   browser: close current tab
   *
   * on both, delete the sessionToken associated with this session
   *
   * @param {ActionsObservable<ReduxAction>} action$
   * @param {Store} store
   * @param dependencies
   */
  onLeaveSession(action$, store, dependencies) {
    return action$.ofType(this.actions.leaveSession.requestSuccess.TYPE)
      .filter(() => false)
      .catch(epicErrorHandler(this.logger, 'VideoSessionEpics.onLeaveSession'));
  }
}



// WEBPACK FOOTER //
// ./src/js/comments/epics/videoSessionEpics.js